import { useForm } from "react-hook-form";
import { cn } from "../../../utils/css";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Label,
  Input,
  Button,
  Checkbox,
  RichTextEditor,
} from "@pushpress/pushpress-ui";
import type { SendEmailForm } from "./CalendarDetail";

type SendEmailToAttendeesModalProps = {
  visible: boolean;
  loading: boolean;
  messageInfo: string;
  onClose: () => void;
  onSendClick: (data: SendEmailForm) => void;
};

export default function SendEmailToAttendeesModal({
  visible,
  loading,
  messageInfo,
  onClose,
  onSendClick,
}: SendEmailToAttendeesModalProps) {
  const {
    handleSubmit,
    register,
    setError,
    clearErrors,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SendEmailForm>({
    mode: "onTouched",
    defaultValues: {
      subject: "",
      message: "",
      bccYourself: false,
      bccStaff: false,
      bccAdmins: false,
    },
  });

  const handleSendMessage = (data: SendEmailForm) => {
    const { subject, message } = data;
    let hasError = false;

    if (subject === "") {
      setError("subject", { message: "Required" });
      hasError = true;
    }

    if (message === "" || message === "<p></p>") {
      setError("message", { message: "Required" });
      hasError = true;
    }

    if (hasError) {
      return;
    }

    onSendClick(data);
  };

  const handleModalClose = () => {
    onClose();
    reset();
    clearErrors();
  };

  return (
    <Dialog open={visible} onOpenChange={handleModalClose} modal>
      <DialogContent
        aria-describedby={undefined}
        align="center"
        className="sm:tw-max-w-[800px]"
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
        variant="noPadding"
      >
        <DialogHeader className="tw-pt-4 tw-pb-5 tw-border-b">
          <DialogTitle className="tw-text-neutral-800 tw-text-center !tw-font-medium tw-text-xl">
            Email attendees
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleSendMessage)}>
          <div className="tw-overflow-y-auto !tw-max-h-[calc(80vh-160px)] tw-px-6">
            <div className="tw-grid tw-gap-4">
              <div className="tw-flex tw-pb-2 tw-pt-2 tw-text-primary">
                {messageInfo}
              </div>
              <div className="tw-grid tw-grid-cols-4 tw-items-center tw-gap-4 tw-border-b tw-pb-5 tw-pt-2">
                <Label htmlFor="bcc" className="tw-text-left tw-my-auto">
                  BCC
                </Label>
                <div className="tw-col-span-3 tw-grid sm:tw-grid-cols-[1fr_1fr_1fr] tw-gap-4 sm:tw-gap-2">
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Checkbox
                      {...register("bccYourself")}
                      onCheckedChange={(checked: boolean) => {
                        setValue("bccYourself", checked);
                      }}
                      checked={watch("bccYourself")}
                      id="bcc-yourself"
                    />
                    <Label htmlFor="bcc-yourself" className="tw-mb-0">
                      BCC yourself
                    </Label>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Checkbox
                      {...register("bccStaff")}
                      onCheckedChange={(checked: boolean) => {
                        setValue("bccStaff", checked);
                      }}
                      checked={watch("bccStaff")}
                      id="bcc-staff"
                    />
                    <Label htmlFor="bcc-staff" className="tw-mb-0">
                      BCC staff
                    </Label>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Checkbox
                      {...register("bccAdmins")}
                      onCheckedChange={(checked: boolean) => {
                        setValue("bccAdmins", checked);
                      }}
                      checked={watch("bccAdmins")}
                      id="bcc-admins"
                    />
                    <Label htmlFor="bcc-admins" className="tw-mb-0">
                      BCC admins
                    </Label>
                  </div>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-4 tw-items-center tw-gap-4 tw-border-b tw-pb-2 tw-pt-2">
                <Label htmlFor="subject" className="tw-text-left">
                  Subject
                </Label>
                <div className="tw-col-span-3">
                  <Input
                    {...register("subject")}
                    className={
                      errors.subject
                        ? "tw-border-red-500 tw-focus-visible:tw-ring-error"
                        : ""
                    }
                  />
                  <div className="tw-h-4">
                    {errors.subject && (
                      <div className="tw-text-xs tw-text-error">
                        {errors.subject.message}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-4 tw-items-center tw-gap-4 tw-pt-2 tw-pb-6">
                <Label htmlFor="message" className="tw-text-left tw-my-auto">
                  Message
                </Label>
                <div className="tw-col-span-3 sm:tw-h-[220px] max-sm:tw-h-[250px]">
                  <RichTextEditor
                    {...register("message")}
                    onChange={(html: string) => {
                      setValue("message", html);
                      clearErrors("message");
                    }}
                    enableStrikethrough={false}
                    enableHighlight={false}
                    enableDivider={false}
                    enableTextColor={false}
                    height="tw-h-[160px]"
                    className={cn(
                      "!tw-text-base tw-text-black",
                      "[&_p]:tw-my-0 [&_h1]:tw-m-0 [&_h2]:tw-m-0 [&_h3]:tw-m-0",
                      "[&_ul]:tw-my-0 [&_ol]:tw-my-0 [&_li]:tw-my-0 [&_li]:marker:tw-text-black",
                      errors.message
                        ? "tw-border-[1px] tw-rounded-sm tw-border-error focus-visible:tw-outline-none"
                        : "",
                    )}
                  />
                  {errors.message && (
                    <div className="tw-h-4 tw-text-xs tw-text-error tw-mt-1">
                      {errors.message.message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="tw-flex tw-justify-end tw-border-t tw-py-6 tw-px-6">
            <Button
              type="submit"
              size="lg"
              data-testid="send-message-button"
              disabled={loading}
              isSubmitting={loading}
            >
              Send
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
